<template>
   <div data-cy="date-v1" class="mt-4 md:flex md:gap-12">
      <DateInputs
         :id="block.id"
         :isFormatDDMMYY="state.requiredDateFormat === 'dd-mm-yyyy'"
         :isRequired="!!props.block.isRequired"
         :validations="props.block.data?.validations || {}"
         :showBlockErrors="showBlockErrors"
         :existingAnswer="{ year: state.input.year, month: state.input.month, day: state.input.day }"
         @inputs-update="processInputs"
      />
   </div>
</template>

<script setup lang="ts">
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import type { dateInputsEmits } from "@/components/flowsel/_inputs/DateInputs.vue";
import { getFormattedFromDateInput, getDateInputFromFormatted } from "./date-utils";

export interface DateAnswerData {
   date: string;
}

export interface DateBlock extends FlowselBlock {
   data: {
      displayFormat: "dd-mm-yyyy" | "yyyy-mm-dd";
      validations: {
         min?: string;
         max?: string;
      };
   };
}

interface Props {
   block: DateBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const DATE_FORMAT = "YYYY-MM-DD";
const emit = defineEmits(["update-valid", "update-answer"]);

const props = defineProps<Props>();

const state = reactive({
   input: {
      day: "",
      month: "",
      year: "",
   },
   inputsAreValid: false,
   requiredDateFormat: unref(props.block.data.displayFormat),
});

// Methods
// -----------------------------------------
// update the inputs with any existing answer data
const initialiseComponent = function () {
   const existingDate = props.userBlockAnswer?.data?.date;

   if (existingDate) {
      state.input = getDateInputFromFormatted(existingDate, DATE_FORMAT);
   }
};

function processInputs(inputsInfo: dateInputsEmits) {
   if (!inputsInfo.valid) {
      // an input is invalid (some missing required fields / has errors)
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer when this question is not required.
      // For no answer, a different data format is required by the BE.
      let answerToSubmit: DateAnswerData | null = null;
      if (inputsInfo.year && inputsInfo.month && inputsInfo.day) {
         state.input.year = inputsInfo.year;
         state.input.month = inputsInfo.month;
         state.input.day = inputsInfo.day;
         answerToSubmit = { date: getFormattedFromDateInput(state.input, DATE_FORMAT) };
      }
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

// On load, check for an existing answer or for user data
initialiseComponent();
</script>
